import { useRef, useState } from "react";
import { Lock, ExternalLink, MessageCircle, Ruler } from "lucide-react";
import { fileUrl, money } from "@/lib/api";
import { SketchPaths } from "@/components/board/Sketch";

export const NOTE_COLORS = ["#FFFFFF", "#F1F3F5", "#E6ECF0", "#E8EEE6", "#EFE9E4"];
export const TEXTURES = ["wood", "stone", "linen", "metal", "tile"];
const TILTED = new Set(["note", "swatch"]);

export function tiltOf(el) {
  if (el.data?.straight || !TILTED.has(el.type)) return 0;
  let h = 0;
  for (const c of el.id) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return ((h % 500) / 100 - 2.5).toFixed(2);
}

function BeforeAfter({ before, after, alt }) {
  const [pos, setPos] = useState(50);
  return (
    <div className="relative h-full w-full select-none" onPointerMove={(e) => { if (e.buttons) { const r = e.currentTarget.getBoundingClientRect(); setPos(Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100))); } }} data-testid="before-after">
      <img src={fileUrl(after)} alt={alt} draggable={false} className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}><img src={fileUrl(before)} alt="" draggable={false} className="h-full w-full object-cover" style={{ width: `${10000 / Math.max(pos, 1)}%`, maxWidth: "none" }} /></div>
      <div className="absolute inset-y-0 w-0.5 bg-white shadow" style={{ left: `${pos}%` }}><span className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-stone-900/70" /></div>
      <span className="absolute left-2 top-2 rounded bg-black/55 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-white">Before</span>
      <span className="absolute right-2 top-2 rounded bg-black/55 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-white">After</span>
    </div>
  );
}

function Content({ el, editing, onText }) {
  const d = el.data || {};
  switch (el.type) {
    case "note":
      return editing ? (
        <textarea autoFocus defaultValue={d.text} onBlur={(e) => onText(e.target.value)} onPointerDown={(e) => e.stopPropagation()}
          className="h-full w-full resize-none bg-transparent p-4 font-serif text-lg leading-snug outline-none" data-testid={`note-editor-${el.id}`} />
      ) : <p className="h-full overflow-hidden whitespace-pre-wrap p-4 pt-5 font-serif text-lg leading-snug text-stone-800">{d.text || "Double-click to write"}</p>;
    case "heading":
      return (
        <div className="flex h-full flex-col justify-end p-3">
          <p className="font-serif text-4xl leading-none text-stone-800">{d.title || "Room title"}</p>
          {d.subtitle && <p className="mt-2 text-[11px] uppercase tracking-[0.3em] text-stone-500">{d.subtitle}</p>}
          <span className="mt-3 h-px w-16 bg-[hsl(var(--accent))]" />
        </div>
      );
    case "dimension":
      return (
        <div className="flex h-full items-center justify-center gap-2 rounded-full border border-dashed border-stone-500 bg-white/70 px-3 font-mono text-sm text-stone-700">
          <Ruler className="h-4 w-4" />{d.text || `12' 0" × 9' 6"`}
        </div>
      );
    case "palette":
      return (
        <div className="flex h-full flex-col card-paper">
          <div className="flex flex-1">{(d.colors || []).map((c, i) => <div key={`${c.hex}-${i}`} className="flex-1" style={{ background: c.hex }} title={c.name} />)}</div>
          <div className="px-3 py-2"><p className="truncate text-xs font-semibold text-stone-800">{d.name || "Palette"}</p><p className="truncate text-[10px] text-stone-500">{(d.colors || []).map((c) => c.name).join(" · ")}</p></div>
        </div>
      );
    case "swatch":
      return (
        <div className="flex h-full flex-col">
          <div className="flex-1" style={{ background: d.hex }} />
          <div className="card-paper px-3 py-2 text-stone-800"><p className="truncate text-sm font-semibold">{d.name}</p><p className="truncate font-mono text-[10px] uppercase text-stone-500">{d.brand} · {d.code}</p></div>
        </div>
      );
    case "image":
      return (
        <div className="flex h-full flex-col bg-white p-2.5 pb-0">
          <div className="relative min-h-0 flex-1 overflow-hidden bg-stone-100">{d.url && <img src={fileUrl(d.url)} alt={d.caption || ""} draggable={false} className="h-full w-full object-cover" />}</div>
          <p className="flex h-8 items-center justify-center gap-2 truncate px-1 pt-1.5 text-center font-mono text-[10px] uppercase tracking-wider text-stone-600"><span className="truncate">{d.caption || d.name || " "}</span>{d.link && <a href={d.link} target="_blank" rel="noreferrer" onPointerDown={(e) => e.stopPropagation()} className="inline-flex shrink-0 items-center gap-1 normal-case tracking-normal text-primary underline" title={d.link} data-testid="card-link"><ExternalLink className="h-3 w-3" />Open</a>}</p>
        </div>
      );
    case "material":
      return (
        <div className="flex h-full flex-col">
          {d.url ? <img src={fileUrl(d.url)} alt="" draggable={false} className="min-h-0 flex-1 object-cover" /> : <div className={`flex-1 ${TEXTURES.includes(d.texture) ? d.texture : "linen"}`} style={d.hex ? { background: d.hex } : {}} />}
          <div className="card-paper flex items-center justify-between gap-2 px-3 py-2 text-stone-800">
            <div className="min-w-0"><p className="truncate text-sm font-semibold">{d.name}</p><p className="truncate text-[10px] text-stone-500">{[d.supplier, d.finish].filter(Boolean).join(" · ") || "Material"}</p></div>
            {d.price ? <span className="shrink-0 rounded-full bg-stone-800 px-2 py-0.5 font-mono text-[10px] text-white">{money(d.price)}</span> : null}
          </div>
        </div>
      );
    default:
      return (
        <div className="flex h-full flex-col bg-white text-stone-800">
          <div className="relative min-h-0 flex-1 bg-stone-100">
            {d.before_url && d.url ? <BeforeAfter before={d.before_url} after={d.url} alt={d.name} /> : d.url ? <img src={fileUrl(d.url)} alt="" draggable={false} className="h-full w-full object-cover" /> : null}
            {d.ai && <span className="absolute bottom-2 left-2 rounded bg-white/85 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-stone-700">AI reimagined</span>}
          </div>
          <div className="space-y-0.5 px-3 py-2">
            <p className="text-[9px] uppercase tracking-[0.2em] text-stone-500">{d.finish || "Furniture"}</p>
            <p className="truncate text-sm font-semibold">{d.name}</p>
            <p className="flex items-center justify-between gap-1 text-[11px] text-stone-500"><span className="truncate">{d.supplier}</span>{d.price ? <span className="font-mono">{money(d.price)}</span> : null}</p>
            {d.link && <a href={d.link} target="_blank" rel="noreferrer" onPointerDown={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-[11px] text-primary underline" title={d.link} data-testid="card-link"><ExternalLink className="h-3 w-3" />Open link</a>}
          </div>
        </div>
      );
  }
}

const frame = (el) => {
  const base = "absolute overflow-visible";
  if (el.type === "heading" || el.type === "dimension") return `${base}`;
  if (el.type === "note") return `${base} fold item-shadow rounded-sm`;
  if (el.type === "image") return `${base} item-shadow rounded-sm`;
  if (el.type === "swatch") return `${base} pin item-shadow rounded-md`;
  return `${base} item-shadow rounded-lg`;
};
// Legacy cream/peach note colours (pre-round-29 boards) render as the current neutral palette.
const LEGACY_NOTE = { "#F4EEDF": "#FFFFFF", "#E1E8D8": "#E8EEE6", "#DCE6EA": "#E6ECF0", "#F2DCCB": "#EFE9E4", "#E6E0DC": "#F1F3F5" };
export const noteColor = (c) => LEGACY_NOTE[(c || "").toUpperCase()] || c || NOTE_COLORS[0];
const bg = (el) => (el.type === "note" ? noteColor(el.data?.color) : ["heading", "dimension"].includes(el.type) ? "transparent" : "#fff");

export function CanvasItem({ el, editable, selected, editing, commentCount, zoom = 1, onSelect, onMove, onCommit, onEdit, onText, children }) {
  const start = useRef(null);
  const down = (mode) => (e) => {
    onSelect(el.id, e.shiftKey);
    if (!editable || editing) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    start.current = { mode, px: e.clientX, py: e.clientY, x: el.x, y: el.y, w: el.w, h: el.h, moved: false };
  };
  const move = (e) => {
    const s = start.current;
    if (!s) return;
    const dx = (e.clientX - s.px) / zoom, dy = (e.clientY - s.py) / zoom;
    s.moved = true;
    if (s.mode === "drag") onMove(el.id, { x: Math.max(0, s.x + dx), y: Math.max(0, s.y + dy) });
    else if ((el.type === "image" || el.type === "furniture") && !e.shiftKey) { const w = Math.max(80, s.w + dx); onMove(el.id, { w, h: Math.max(60, Math.round((w - 20) * ((s.h - 40) / Math.max(1, s.w - 20)) + 40)) }); }
    else onMove(el.id, { w: Math.max(80, s.w + dx), h: Math.max(60, s.h + dy) });
  };
  const up = () => { if (start.current?.moved) onCommit(); start.current = null; };
  const tilt = selected ? 0 : tiltOf(el);
  return (
    <div role="button" tabIndex={0} aria-label={`${el.type} ${el.data?.name || el.data?.text || el.data?.title || ""}`} data-testid={`canvas-item-${el.id}`}
      onPointerDown={down("drag")} onPointerMove={move} onPointerUp={up} onDoubleClick={() => editable && onEdit(el.id)}
      onFocus={() => onSelect(el.id, false)}
      className={`${frame(el)} settle touch-none select-none outline-none transition-[transform,box-shadow] duration-200 ${editable ? "cursor-grab active:cursor-grabbing" : ""} ${selected ? "ring-2 ring-accent ring-offset-2 ring-offset-transparent" : "hover:-translate-y-0.5"}`}
      style={{ left: el.x, top: el.y, width: el.w, height: el.h, zIndex: selected ? 999 : el.z || 1, background: bg(el), "--tilt": `rotate(${tilt}deg)`, transform: `rotate(${tilt}deg)` }}>
      <div className="h-full w-full overflow-hidden rounded-[inherit]"><Content el={el} editing={editing} onText={onText} /></div>
      {children}
      {editable && el.data?.team_note && <span className="absolute -left-2 -top-2 z-10 flex items-center gap-1 rounded-full bg-stone-900 px-1.5 py-0.5 text-[10px] text-white shadow" title={el.data.team_note} data-testid={`team-note-badge-${el.id}`}><Lock className="h-3 w-3" />Team</span>}
      {commentCount > 0 && <span className="absolute -right-2 -top-2 z-10 flex items-center gap-0.5 rounded-full bg-stone-900 px-1.5 py-0.5 text-[10px] text-white shadow"><MessageCircle className="h-3 w-3" />{commentCount}</span>}
      {editable && selected && !editing && (
        <span onPointerDown={down("resize")} onPointerMove={move} onPointerUp={up} data-testid={`resize-handle-${el.id}`}
          className="absolute -bottom-1.5 -right-1.5 z-10 h-5 w-5 cursor-se-resize rounded-full border-2 border-white bg-accent shadow" />
      )}
    </div>
  );
}

export function Connections({ elements, connections }) {
  const byId = Object.fromEntries(elements.map((e) => [e.id, e]));
  return (
    <svg className="pointer-events-none absolute inset-0 h-full w-full overflow-visible" data-testid="canvas-connections">
      {connections.map((c) => {
        const a = byId[c.from], b = byId[c.to];
        if (!a || !b) return null;
        const x1 = a.x + a.w / 2, y1 = a.y + a.h / 2, x2 = b.x + b.w / 2, y2 = b.y + b.h / 2;
        return <path key={c.id} d={`M${x1},${y1} Q${(x1 + x2) / 2 + (y2 - y1) * 0.15},${(y1 + y2) / 2 - (x2 - x1) * 0.15} ${x2},${y2}`} fill="none" stroke="hsl(28 42% 30%)" strokeWidth="1.5" strokeDasharray="4 6" strokeLinecap="round" />;
      })}
    </svg>
  );
}

export function StaticStage({ elements, connections, sketches = [] }) {
  const w = Math.max(400, ...elements.map((e) => e.x + e.w)) + 40;
  const h = Math.max(300, ...elements.map((e) => e.y + e.h)) + 40;
  return { w, h, node: (
    <div className="relative" style={{ width: w, height: h }}>
      <Connections elements={elements} connections={connections} />
      {sketches.length > 0 && <svg className="pointer-events-none absolute inset-0" width={w} height={h} viewBox={`0 0 ${w} ${h}`}><SketchPaths strokes={sketches} /></svg>}
      {elements.map((el, i) => (
        <div key={el.id} className={`${frame(el)} settle`} data-testid={`canvas-item-${el.id}`} style={{ left: el.x, top: el.y, width: el.w, height: el.h, zIndex: el.z || 1, background: bg(el), "--tilt": `rotate(${tiltOf(el)}deg)`, transform: `rotate(${tiltOf(el)}deg)`, animationDelay: `${Math.min(i, 12) * 60}ms` }}>
          <div className="h-full w-full overflow-hidden rounded-[inherit]"><Content el={el} /></div>
        </div>
      ))}
    </div>
  ) };
}
